import { Capacitor } from '@capacitor/core';
import { notificationAPI } from '../api/api';

const SEEN_MASRAF_KEY = 'appNotifSeenMasrafIds';
const SEEN_MUVEKKIL_BELGE_KEY = 'appNotifSeenMuvekkilBelgeIds';
const CHANNEL_ID = 'ozturk-hukuk';

/* Bildirim sesi. iOS'ta 'sound' verilmezse bildirim SESSIZ gelir —
   ekranda gorunur ama telefon hic ses cikarmaz. Dosya public/ klasorunde
   duruyor ve derlemede uygulama paketine kopyalaniyor. */
const BILDIRIM_SESI = 'bildirim.wav';


const isNative = () =>
  typeof Capacitor.isNativePlatform === 'function' && Capacitor.isNativePlatform();

const nid = (kind, id, extra = '') => {
  const s = `${kind}-${id}-${extra}`;
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h << 5) - h + s.charCodeAt(i);
  return Math.abs(h) % 2000000000 || 1;
};

const parseHearingDate = (value) => {
  if (!value) return null;
  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      const [datePart, timePartRaw] = value.split('T');
      const [y, m, d] = datePart.split('-').map(Number);
      const timePart = (timePartRaw || '00:00:00').split(/[.+Z-]/)[0];
      const [hh, mm, ss] = timePart.split(':').map(Number);
      return new Date(y, m - 1, d, hh || 0, mm || 0, ss || 0);
    }
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? null : dt;
  } catch (e) {
    return null;
  }
};

const readSeen = (anahtar) => {
  try {
    const raw = JSON.parse(localStorage.getItem(anahtar) || '[]');
    return new Set((raw || []).map(Number));
  } catch (e) {
    return new Set();
  }
};

const writeSeen = (anahtar, ids) => {
  try {
    localStorage.setItem(anahtar, JSON.stringify([...ids].slice(-80)));
  } catch (e) {}
};

const readSeenMasraf = () => readSeen(SEEN_MASRAF_KEY);
const writeSeenMasraf = (ids) => writeSeen(SEEN_MASRAF_KEY, ids);

export const initAppNotifications = async (navigate) => {
  if (!isNative()) return;

  let LocalNotifications;
  try {
    ({ LocalNotifications } = await import('@capacitor/local-notifications'));
  } catch (e) {
    return;
  }

  try {
    let perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
      perm = await LocalNotifications.requestPermissions();
    }
    if (perm.display !== 'granted') return;

    try {
      await LocalNotifications.createChannel({
        id: CHANNEL_ID,
        name: 'Öztürk Hukuk',
        description: 'Duruşma ve masraf bildirimleri',
        importance: 5,
        visibility: 1
      });
    } catch (e) {}

    await LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
      const data = event?.notification?.extra || {};
      if (data.type === 'hearing' && data.caseFileId) {
        navigate(`/case-files/${data.caseFileId}`);
      } else if (data.type === 'masraf') {
        navigate(data.caseFileId ? `/case-files/${data.caseFileId}` : '/masraf-talepleri');
      } else if (data.type === 'muvekkil-evrak' && data.caseFileId) {
        // Muvekkilin yukledigi evrak: dogrudan dosyasi acilsin.
        navigate(`/case-files/${data.caseFileId}`);
      }
    });
  } catch (e) {}

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    let pushPerm = await PushNotifications.checkPermissions();
    if (pushPerm.receive !== 'granted') {
      pushPerm = await PushNotifications.requestPermissions();
    }
    if (pushPerm.receive === 'granted') {
      await PushNotifications.addListener('registration', async (token) => {
        try {
          await notificationAPI.registerDevice(token.value, Capacitor.getPlatform());
        } catch (err) {}
      });
      await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
        const data = event?.notification?.data || {};
        if (data.type === 'hearing' && data.caseFileId) {
          navigate(`/case-files/${data.caseFileId}`);
        } else if (data.type === 'masraf') {
          navigate(data.caseFileId ? `/case-files/${data.caseFileId}` : '/masraf-talepleri');
        } else if (data.type === 'muvekkil-evrak' && data.caseFileId) {
          navigate(`/case-files/${data.caseFileId}`);
        } else if (data.type === 'gorev') {
          // Kişiye atanan görev: "Bana Atanan Görevler" sayfası.
          navigate('/tasks');
        } else if (data.type === 'is') {
          navigate('/job-list');
        }
      });
      await PushNotifications.register();
    }
  } catch (e) {}

  await syncAppNotifications(LocalNotifications);

  try {
    const { App } = await import('@capacitor/app');
    App.addListener('resume', () => {
      syncAppNotifications(LocalNotifications);
    });
  } catch (e) {}
};

export const syncAppNotifications = async (LocalNotificationsLib) => {
  if (!isNative()) return;
  let LocalNotifications = LocalNotificationsLib;
  if (!LocalNotifications) {
    try {
      ({ LocalNotifications } = await import('@capacitor/local-notifications'));
    } catch (e) {
      return;
    }
  }

  let feed;
  try {
    feed = await notificationAPI.getFeed();
  } catch (e) {
    return;
  }

  const hearings = feed?.hearings || feed?.Hearings || [];
  const masraflar = feed?.masrafRequests || feed?.MasrafRequests || [];
  const now = Date.now();
  const schedules = [];

  hearings.forEach((h) => {
    const hearingAt = parseHearingDate(h.durusmaTarihi || h.DurusmaTarihi);
    if (!hearingAt) return;
    const caseFileId = h.caseFileId || h.CaseFileId;
    const foyNo = h.foyNo || h.FoyNo || '';
    const muvekkil = h.muvekkil || h.Muvekkil || '';
    const titleBase = `${foyNo}${foyNo && muvekkil ? ' — ' : ''}${muvekkil}`.trim();
    const extra = { type: 'hearing', caseFileId: String(caseFileId || '') };

    const dayAt = new Date(hearingAt.getTime() - 24 * 60 * 60 * 1000);
    const hourAt = new Date(hearingAt.getTime() - 60 * 60 * 1000);

    if (dayAt.getTime() > now) {
      schedules.push({
        id: nid('hd', caseFileId, hearingAt.toISOString()),
        title: 'Yarın duruşma var',
        body: `${titleBase} · ${hearingAt.toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`,
        schedule: { at: dayAt },
        extra,
        channelId: CHANNEL_ID,
      sound: BILDIRIM_SESI,
      iosInterruptionLevel: 'active'
      });
    }
    if (hourAt.getTime() > now) {
      schedules.push({
        id: nid('hh', caseFileId, hearingAt.toISOString()),
        title: 'Duruşmaya 1 saat kaldı',
        body: `${titleBase} · ${hearingAt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}`,
        schedule: { at: hourAt },
        extra,
        channelId: CHANNEL_ID,
      sound: BILDIRIM_SESI,
      iosInterruptionLevel: 'active'
      });
    }
  });

  const seen = readSeenMasraf();
  const incomingIds = masraflar.map((m) => Number(m.id ?? m.Id)).filter(Boolean);
  const firstSync = seen.size === 0;
  incomingIds.forEach((id) => {
    if (firstSync) {
      seen.add(id);
      return;
    }
    if (seen.has(id)) return;
    seen.add(id);
    const item = masraflar.find((m) => Number(m.id ?? m.Id) === id);
    const foyNo = item?.foyNo || item?.FoyNo || '';
    const muvekkil = item?.muvekkil || item?.Muvekkil || '';
    const tur = item?.masrafTuru || item?.MasrafTuru || 'Masraf';
    schedules.push({
      id: nid('mr', id),
      title: 'Yeni masraf talebi',
      body: `${foyNo}${foyNo && muvekkil ? ' — ' : ''}${muvekkil} · ${tur}`.trim(),
      extra: { type: 'masraf', caseFileId: String(item?.caseFileId || item?.CaseFileId || ''), requestId: String(id) },
      channelId: CHANNEL_ID,
      sound: BILDIRIM_SESI,
      iosInterruptionLevel: 'active'
    });
  });
  writeSeenMasraf(seen);

  /*
    MUVEKKILIN YUKLEDIGI EVRAK
    --------------------------
    Muvekkil kendi dosyasina belge gonderdiginde buronun haberi
    olmuyordu. Sunucu artik bunlari bildirim akisina koyuyor; burada
    daha once gorulmemis olanlar icin telefonda bildirim cikariyoruz.

    ILK ACILISTA SUSUYORUZ: masraf taleplerinde oldugu gibi, ozellik
    ilk kez calistiginda gecmisteki butun yuklemeler icin ust uste
    bildirim yagmasin diye o anki liste "gorulmus" sayiliyor.
  */
  const muvekkilBelgeler = feed?.muvekkilBelgeler || feed?.MuvekkilBelgeler || [];
  const belgeGorulen = readSeen(SEEN_MUVEKKIL_BELGE_KEY);
  const belgeIlkSenkron = belgeGorulen.size === 0;

  muvekkilBelgeler.forEach((b) => {
    const id = Number(b.id ?? b.Id);
    if (!id) return;
    if (belgeIlkSenkron || belgeGorulen.has(id)) { belgeGorulen.add(id); return; }
    belgeGorulen.add(id);

    const foyNo = b.foyNo || b.FoyNo || '';
    const muvekkil = b.muvekkil || b.Muvekkil || '';
    schedules.push({
      id: nid('mb', id),
      title: 'Müvekkil evrak yükledi',
      body: `${foyNo}${foyNo && muvekkil ? ' — ' : ''}${muvekkil}`.trim() || 'Dosyanıza yeni evrak eklendi.',
      extra: {
        type: 'muvekkil-evrak',
        caseFileId: String(b.caseFileId || b.CaseFileId || ''),
      },
      channelId: CHANNEL_ID,
      sound: BILDIRIM_SESI,
      iosInterruptionLevel: 'active'
    });
  });
  writeSeen(SEEN_MUVEKKIL_BELGE_KEY, belgeGorulen);

  if (schedules.length === 0) return;
  try {
    await LocalNotifications.schedule({ notifications: schedules.slice(0, 60) });
  } catch (e) {}
};
